import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

// Base da API para todas as telas. O token não vai chamada a chamada: o AuthProvider põe no header padrão
// do axios antes de qualquer rota protegida abrir (o ProtectedRoute espera o `loading`) e tira no logout.
export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Perfis de cada área, lidos pelas rotas (App.js) e pelo menu (Layout.js): o menu só mostra o que a rota abre.
export const VENDAS = ['comercial', 'master', 'sales_ops'];
// Edição de pedido: vendas + administrativo, que corrige pela Gestão de Pedidos
// (ex.: endereço que o vendedor não altera mais depois do checkout gerado).
export const EDITAR_PEDIDO = [...VENDAS, 'administrativo'];
export const OPERACIONAL = ['administrativo', 'master'];
export const EMPRESAS = ['master', 'administrativo'];
export const SO_MASTER = ['master'];
export const PROPOSTAS_LEITURA = ['comercial', 'master', 'sales_ops', 'administrativo', 'financiamentos'];
// Todos menos o Financiamentos, que entra direto nas Propostas.
export const DASHBOARD = ['comercial', 'master', 'administrativo', 'sales_ops'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      
      toast.success('Login realizado com sucesso!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
      return false;
    }
  }, []);

  // Login com Google: recebe {access_token} (popup OAuth) e troca pelo token do sistema.
  // 403 = aguardando o Master vincular.
  const loginWithGoogle = useCallback(async (payload) => {
    try {
      const response = await axios.post(`${API_URL}/auth/google`, payload);
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setToken(access_token);
      setUser(userData);

      toast.success('Login realizado com sucesso!');
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao entrar com o Google';
      toast[error.response?.status === 403 ? 'info' : 'error'](detail, { duration: 8000 });
      return false;
    }
  }, []);


  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    toast.success('Logout realizado com sucesso');
  }, []);

  const value = useMemo(() => ({
    user, loading, login, loginWithGoogle, logout
  }), [user, loading, login, loginWithGoogle, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
